/**
 * The house runs on Berlin time: tariff windows, "today", months and years are local
 * calendar periods. One definition for all of them, explicit about the zone so it does
 * not depend on the process or browser time zone (mac-server runs Europe/Berlin and
 * server/index.js pins it; a test runner, a container or a phone abroad might not).
 * Shared by the server (server/services/localTime.js re-exports it) and the dashboard.
 *
 * A local day is [midnight, next midnight): 24 h, except 23 h on the last Sunday of
 * March and 25 h on the last Sunday of October. Never add 24 h to get the next day.
 */

export const TZ = 'Europe/Berlin'

const fmt = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
})

const pad = n => String(n).padStart(2, '0')

function intlParts(t) {
  const p = {}
  for (const { type, value } of fmt.formatToParts(new Date(t))) if (type !== 'literal') p[type] = Number(value)
  return p
}

// Berlin is a whole number of hours ahead of UTC and switches on a whole UTC hour, so
// the offset is looked up once per UTC hour — Intl is far too slow to call per minute
// of a year of tariff lookups.
const HOUR = 3_600_000
const offsets = new Map()
function offsetOf(t) {
  const h = Math.floor(t / HOUR)
  let o = offsets.get(h)
  if (o === undefined) {
    const p = intlParts(h * HOUR)
    o = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - h * HOUR
    if (offsets.size > 200_000) offsets.clear()
    offsets.set(h, o)
  }
  return o
}

/** Berlin wall-clock parts of an instant: { year, month (1-12), day, hour, minute, second }. */
export function localParts(t) {
  const d = new Date(+t + offsetOf(+t))
  return {
    year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(),
    hour: d.getUTCHours(), minute: d.getUTCMinutes(), second: d.getUTCSeconds(),
  }
}

/**
 * An instant as Berlin wall time with its offset: "2026-10-30T05:00+01:00" (seconds only
 * when non-zero). Reads as local time at a glance and stays unambiguous — models that get
 * a bare "…T04:00Z" add the wrong offset (summer +2 in winter: 05:00 became 06:00).
 */
export function berlinOffsetIso(t) {
  const ms = +new Date(t)
  if (Number.isNaN(ms)) return t
  const p = localParts(ms), off = Math.round(offsetOf(ms) / 60_000)
  const sec = p.second ? `:${pad(p.second)}` : ''
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}${sec}${off < 0 ? '-' : '+'}${pad(Math.floor(Math.abs(off) / 60))}:${pad(Math.abs(off) % 60)}`
}

/** 'YYYY-MM-DD' of the Berlin day an instant falls in. */
export const localDateStr = t => { const p = localParts(t); return `${p.year}-${pad(p.month)}-${pad(p.day)}` }

/** Minutes since Berlin midnight (wall clock; 02:30 on both sides of the October switch is 150). */
export const localMinutes = t => { const p = localParts(t); return p.hour * 60 + p.minute }

// How far Berlin wall time is ahead of UTC at instant t, in ms.
const offsetAt = t => offsetOf(+t)

/** The instant a Berlin wall-clock time happens. Midnight never falls in a DST gap. */
export function zonedInstant(year, month, day, hour = 0, minute = 0) {
  const wall = Date.UTC(year, month - 1, day, hour, minute)
  let t = wall - offsetAt(wall)
  t = wall - offsetAt(t)   // second pass lands on the right side of a switch
  return new Date(t)
}

const ymd = s => s.split('-').map(Number)

/** 'YYYY-MM-DD' shifted by whole calendar days. */
export function addDays(dateStr, n) {
  const [y, m, d] = ymd(dateStr)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

/** Berlin midnight starting `dateStr`. */
export const localMidnight = dateStr => { const [y, m, d] = ymd(dateStr); return zonedInstant(y, m, d) }

/** One local day as instants: { start, stop, hours } with hours 23, 24 or 25. */
export function dayRange(dateStr) {
  const start = localMidnight(dateStr), stop = localMidnight(addDays(dateStr, 1))
  return { start, stop, hours: (stop - start) / 3_600_000 }
}

/** Local calendar month (1-12) and year as instants. */
export const monthRange = (year, month) => ({
  start: zonedInstant(year, month, 1),
  stop: month === 12 ? zonedInstant(year + 1, 1, 1) : zonedInstant(year, month + 1, 1),
})
export const yearRange = year => ({ start: zonedInstant(year, 1, 1), stop: zonedInstant(year + 1, 1, 1) })

/** Today's Berlin date and its start, as of `now`. */
export const todayStr = (now = Date.now()) => localDateStr(now)
export const todayStart = (now = Date.now()) => localMidnight(localDateStr(now))

// Flux: a window that cuts at Berlin midnight takes `location: FLUX_BERLIN` (the script
// needs FLUX_TZ_IMPORT at the top). Per window, not as a global option — a global
// location makes every 1-minute window zone-aware and a year's query five times slower.
export const FLUX_TZ_IMPORT = 'import "timezone"\n'
export const FLUX_BERLIN = `timezone.location(name: "${TZ}")`
